<template>
    <div class="eligibility-container">
        <span>
            {{ insightsSegmentEligibilityText }}
        </span>
        <pendo-loading-indicator
            :loading="loading"
            size="small" />
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { LOADING_STATES } from '@/constants';
import { connectAggregationToState, withComponent } from '@pendo/agg-connect';
import { PendoLoadingIndicator } from '@pendo/components';
import { mapMutations } from 'vuex';
import checkInsightsSegmentEligibility from '@/insights/insights-segment-eligibility-aggregation/insights-segment-eligibility.aggregation';

const EMPTY_ELIGIBILITY_VALUE = '--';
const MIN_ACCOUNTS_IN_SEGMENT = 580;
const MIN_CHURNED_ACCOUNTS = 75;
const MIN_ACTIVE_ACCOUNTS = 2;
const { LOADING, REJECTED, RESOLVED } = LOADING_STATES;

export default {
    name: 'InsightsSegmentEligibility',
    components: {
        PendoLoadingIndicator,
    },
    props: {
        segment: {
            type: Object,
            default: null,
        },
        appId: {
            type: [String, Number],
            default: '',
        },
        isValid: {
            type: Boolean,
            default: true,
        },
    },
    data () {
        return {
            insightsSegmentEligibilityAgg$: undefined,
            debouncedSegment: undefined,
            segmentEligibility: {
                hasEnoughAccounts: false,
                ninetyDayRule: false,
                thirtyDayRule: false,
            },
            loading: false,
        };
    },
    computed: {
        computedSegment () {
            if (isEmpty(this.segment)) {
                return null;
            }

            return { ...this.segment };
        },
        formattedAppId () {
            const stringAppId = this.appId.toString();
            if (stringAppId.indexOf('-') === 0) {
                return `_${stringAppId.substring(1)}`;
            }

            return stringAppId;
        },
        insightsSegmentEligibilityText () {
            if (!this.appId || this.loading) {
                return EMPTY_ELIGIBILITY_VALUE;
            }

            if (this.isInsightSegmentEligible) {
                return this.$t('insightsSegmentEligibility.Has potential');
            }

            return this.$t('insightsSegmentEligibility.No potential');
        },
        isInsightSegmentEligible () {
            const { hasEnoughAccounts, ninetyDayRule, thirtyDayRule } = this.segmentEligibility;

            return hasEnoughAccounts && (ninetyDayRule || thirtyDayRule);
        },
    },
    watch: {
        computedSegment (segment, prevSegment) {
            if (!this.isValid || isEmpty(segment) || segment.name !== prevSegment?.name) {
                return;
            }

            this.debounceSegment(segment);
        },
    },
    created () {
        this.setupInsightsSegmentEligibilityAgg();
        this.setIsInsightsSegmentEligible(true);
        this.debounceSegment = debounce((segment) => {
            this.setDebouncedSegment(segment);
        }, 1500);
    },
    beforeUnmount () {
        this.insightsSegmentEligibilityAgg$.unsubscribe();
    },
    methods: {
        ...mapMutations({
            setIsInsightsSegmentEligible: 'segments/setIsInsightsSegmentEligible',
        }),
        setSegmentEligibility (value) {
            const {
                accountsInSegment,
                numChurned90Day,
                numChurned30Day,
                numActive90Day,
                numActive30Day,
            } = value.data[0][0];
            this.segmentEligibility = {
                hasEnoughAccounts: accountsInSegment >= MIN_ACCOUNTS_IN_SEGMENT,
                ninetyDayRule: numChurned90Day >= MIN_CHURNED_ACCOUNTS && numActive90Day >= MIN_ACTIVE_ACCOUNTS,
                thirtyDayRule: numChurned30Day >= MIN_CHURNED_ACCOUNTS && numActive30Day >= MIN_ACTIVE_ACCOUNTS,
            };
        },
        resetSegmentEligibility () {
            this.segmentEligibility = {
                hasEnoughAccounts: false,
                ninetyDayRule: false,
                thirtyDayRule: false,
            };
        },
        setupInsightsSegmentEligibilityAgg () {
            const agg$ = connectAggregationToState(
                checkInsightsSegmentEligibility,
                withComponent({ segment: 'debouncedSegment', appId: 'formattedAppId' }),
            )({ component: this });

            this.insightsSegmentEligibilityAgg$ = agg$.subscribe(({ status, value }) => {
                this.resetSegmentEligibility();
                switch (status) {
                    case LOADING:
                        this.loading = true;
                        break;
                    case REJECTED:
                        this.loading = false;
                        break;
                    case RESOLVED:
                        this.setSegmentEligibility(value);
                        this.setIsInsightsSegmentEligible(this.isInsightSegmentEligible);
                        this.loading = false;
                }
            });
        },
        setDebouncedSegment (segment) {
            this.loading = false;
            this.debouncedSegment = segment;
        },
    },
};
</script>

<style scoped lang="less">
.eligibility-container {
    display: flex;
    align-items: center;

    span {
        font-weight: 600;
        font-size: 22px;
        line-height: 22px;
        margin-right: 8px;
    }
}
</style>
